/**
 * Vertrag der Artikel- und Designansichten (Stufe E, Teil 1): Liste, Entwurf laden, anlegen und
 * mit Versionsvergleich speichern. Die Vorschau rendert die SPA selbst mit `@pat/design-html`;
 * dafür liefert die API Design, Medien und Hostingbasis mit.
 */
import { z } from 'zod';
import { Article, Block, Design, EbayItemId, IsoDateTime, Version } from '@pat/core/entities';
import { MediaView, PreviewContext, ReviseState } from './content.js';
import { ItemView } from './contract.js';

// Gemeinsame Bausteine und Designansichten liegen in eigenen Dateien; hier weiter erreichbar.
export { MEDIA_ID, MediaView, PreviewContext, ReviseState } from './content.js';
export { DesignDetailResponse, DesignsResponse, DesignSummaryView } from './designs.js';

export const ArticleSummaryView = z.object({
  articleId: z.string(),
  title: z.string(),
  sku: z.string().optional(),
  designId: z.string().optional(),
  designName: z.string().optional(),
  /**
   * Veraltet (bewusste Vertragsänderung, Claude 30.10.2026): die Liste liest nur Artikelköpfe, die Oberfläche zeigt das
   * Feld nicht. Die API liefert bis zum Rückbau (frühestens 31.10.2026, 24 h nach Deploy 12) den Kompatibilitätswert 0 für
   * ältere SPA-Bundles; neue Bundles dürfen sich nicht darauf verlassen.
   */
  blockCount: z.number().int().nonnegative().optional(),
  version: Version,
  /** Zuletzt veröffentlichte Version; fehlt = nie veröffentlicht. */
  publishedVersion: Version.optional(),
  /** core `Article.legacyAppVersion`: 2 = konvertierter App-2-Artikel, 3 = App 3; fehlt = in der App angelegt (oder älterer Import). */
  legacyAppVersion: z.union([z.literal(2), z.literal(3)]).optional(),
  /** Artikelgruppe (`Article.groupId`) und ihr Name; fehlt = ohne Gruppe oder Gruppe nicht (mehr) vorhanden. */
  groupId: z.string().optional(),
  groupName: z.string().optional(),
  /**
   * Vorschaubild der Liste (03.11.2026, wie Legacy): erstes Bild der Bildblöcke als öffentliche https-Adresse; fehlt = kein
   * Bild, noch nicht nachgetragen oder Medium nicht lesbar.
   */
  previewUrl: z.string().url().max(2048).refine(u => u.startsWith('https://')).optional(),
  updatedAt: IsoDateTime,
});
export type ArticleSummaryView = z.infer<typeof ArticleSummaryView>;

/**
 * Herkunft eines Artikels für Liste und Filter (Flori 30.10.2026): `app2` = konvertierter App-2-Artikel (Marker 2),
 * `app3` = übrige importierte (Marker 3 oder Alt-Artikelnummer als Kennung; Bestand ohne Marker gilt als App 3, weil die
 * Umwandlung immer Marker 2 setzt), `app` = in der App angelegt.
 */
export type ArticleOrigin = 'app' | 'app3' | 'app2';
export function articleOrigin(article: { articleId: string; legacyAppVersion?: 2 | 3 | undefined }): ArticleOrigin {
  if (article.legacyAppVersion === 2) return 'app2';
  if (article.legacyAppVersion === 3 || importedArticleNumber(article.articleId) !== undefined) return 'app3';
  return 'app';
}

/** Seitengröße der Artikelliste (Rundgang B3, Flori 29.10.2026: Konten mit mehreren tausend Artikeln). */
export const ARTICLES_PAGE_DEFAULT = 50;
export const ARTICLES_PAGE_MAX = 200;
export const ArticleOriginFilter = z.enum(['all', 'app', 'app3', 'app2']);
export type ArticleOriginFilter = z.infer<typeof ArticleOriginFilter>;
/**
 * `q`: Wörter, die alle in Titel, SKU oder Artikelkennung (Alt-Artikelnummer) vorkommen müssen, ohne Groß-/Kleinschreibung;
 * leer = alle. `cursor` aus `nextCursor` der vorigen Seite (gleiche Suche und Herkunft).
 */
export const ArticlesQuery = z
  .object({
    q: z.string().trim().max(100).optional(),
    origin: ArticleOriginFilter.default('all'),
    /** Gruppe: Kennung oder `none` (ohne Gruppe); fehlt = alle. */
    group: z.string().regex(/^(none|[A-Za-z0-9_-]{1,64})$/).optional(),
    limit: z.coerce.number().int().min(1).max(ARTICLES_PAGE_MAX).default(ARTICLES_PAGE_DEFAULT),
    cursor: z.string().min(1).max(1024).optional(),
  })
  .strict();
export type ArticlesQuery = z.infer<typeof ArticlesQuery>;
const OriginCount = z.number().int().nonnegative();
export const ArticlesResponse = z.object({
  articles: z.array(ArticleSummaryView),
  editable: z.boolean(),
  /** Treffer von Suche und Herkunft über alle Seiten. */
  total: z.number().int().nonnegative(),
  /** Nicht gelöschte Artikel je Herkunft, unabhängig von Suche und Filter (Anzeige des Herkunftsfilters). */
  origins: z.object({ app: OriginCount, app3: OriginCount, app2: OriginCount }),
  /** Fehlt = letzte Seite. */
  nextCursor: z.string().optional(),
});
export type ArticlesResponse = z.infer<typeof ArticlesResponse>;

export const ArticleDetailResponse = z.object({
  article: Article,
  design: Design.optional(),
  /** Veröffentlichte Fassung des Designs (Snapshot `DESIGNREV#`), die der Worker rendert; fehlt = nie veröffentlicht. */
  publishedDesign: Design.optional(),
  /** `article.designId` ist gesetzt, das Design aber nicht (mehr) als eigenes vorhanden. */
  designUnavailable: z.boolean().optional(),
  /**
   * Designfassung, an die der veröffentlichte Stand gebunden ist (`ArticleRevision.designVersion`). Weicht sie
   * von `publishedDesign.version` ab, übernimmt erst ein neuer veröffentlichter Stand das aktuelle Design.
   */
  boundDesignVersion: Version.optional(),
  media: z.record(z.string(), MediaView),
  preview: PreviewContext,
  editable: z.boolean(),
  /** Nur nach dem Speichern: gespeichert, aber Design/Medien nicht nachladbar; die SPA lädt die Ansicht neu. */
  reloadIncomplete: z.boolean().optional(),
});
export type ArticleDetailResponse = z.infer<typeof ArticleDetailResponse>;

export const MediaResponse = z.object({ media: z.record(z.string(), MediaView) });
export type MediaResponse = z.infer<typeof MediaResponse>;

/** Höchstzahl verschiedener Medien je Artikel (Vorschau und Worker laden sie alle). */
export const MAX_ARTICLE_MEDIA = 500;

/**
 * Medien, die Artikel und Design für die Darstellung brauchen: Logo und erstes Bild zuerst, dann
 * Bilder und Video-Vorschaubilder der Blöcke, ohne Dubletten.
 */
export function mediaIdsOf(
  article: { blocks?: readonly Block[] | undefined; firstPictureMediaId?: string | undefined } | undefined,
  design: { logoMediaId?: string | undefined } | undefined,
): string[] {
  const ids = new Set<string>();
  if (design?.logoMediaId) ids.add(design.logoMediaId);
  if (article?.firstPictureMediaId) ids.add(article.firstPictureMediaId);
  for (const block of article?.blocks ?? []) {
    if (block.type === 'picture') for (const picture of block.data.list) if (picture.mediaId) ids.add(picture.mediaId);
    if (block.type === 'video' && block.data.thumbnailMediaId) ids.add(block.data.thumbnailMediaId);
  }
  return [...ids];
}

/** Gültige https-Adresse mit Host (eBay lädt Bilder und Videos nur so). */
export function isHttpsUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && url.hostname.includes('.') && !value.includes(' ');
  } catch {
    return false;
  }
}

/** Kennung neuer Artikel: die SPA erzeugt sie einmal je Anlegevorgang (Wiederholung idempotent). */
export const NEW_ARTICLE_ID = /^art-[0-9a-f]{12,32}$/;
/** Artikel-IDs landen in Schlüsseln (`ARTICLE#<id>`): kein `#`, kurz. */
export const ARTICLE_ID = /^[A-Za-z0-9_-]{1,64}$/;

/**
 * Artikelgruppen (Flori 30.10.2026, Core `ArticleGroup`, Ports Codex-1): reine Ordnung der Artikelliste. Importierte
 * Gruppen tragen die Legacy-Kennung, in der App angelegte `grp-<hex>` aus der SPA (Wiederholung idempotent).
 */
export const GROUP_ID = /^[A-Za-z0-9_-]{1,64}$/;
export const NEW_GROUP_ID = /^grp-[0-9a-f]{12,32}$/;
export const GroupName = z.string().trim().min(1).max(120);
export const ArticleGroupView = z.object({
  groupId: z.string(),
  name: z.string(),
  /** Nicht gelöschte Artikel mit dieser Gruppe. */
  articleCount: z.number().int().nonnegative(),
  /** Kein Artikel verweist mehr auf die Gruppe, auch kein gelöschter (nur dann löscht Data, Codex-1 30.10.2026). */
  deletable: z.boolean(),
  updatedAt: IsoDateTime,
});
export type ArticleGroupView = z.infer<typeof ArticleGroupView>;
export const ArticleGroupsResponse = z.object({ groups: z.array(ArticleGroupView), editable: z.boolean() });
/**
 * `GET /article-groups?counts=false` (prod 30.10.2026): nur Namen, ohne Zähler – die Zähler lesen alle Artikelköpfe des
 * Mandanten (B1: 19 MB). Für Filter und Auswahlfeld; die Gruppenverwaltung lädt die Zähler erst beim Öffnen.
 */
export const ArticleGroupsQuery = z.object({ counts: z.enum(['true', 'false']).optional() }).strict();
export const ArticleGroupNameView = ArticleGroupView.pick({ groupId: true, name: true, updatedAt: true });
export type ArticleGroupNameView = z.infer<typeof ArticleGroupNameView>;
export const ArticleGroupNamesResponse = z.object({ groups: z.array(ArticleGroupNameView), editable: z.boolean() });
export type ArticleGroupNamesResponse = z.infer<typeof ArticleGroupNamesResponse>;
export type ArticleGroupsResponse = z.infer<typeof ArticleGroupsResponse>;
export const ArticleGroupCreateRequest = z.object({ groupId: z.string().regex(NEW_GROUP_ID), name: GroupName }).strict();
export type ArticleGroupCreateRequest = z.infer<typeof ArticleGroupCreateRequest>;
export const ArticleGroupRenameRequest = z.object({ name: GroupName, expectedUpdatedAt: IsoDateTime }).strict();
export type ArticleGroupRenameRequest = z.infer<typeof ArticleGroupRenameRequest>;
export const ArticleGroupDeleteQuery = z.object({ expectedUpdatedAt: IsoDateTime }).strict();
/** Gruppe am Artikel setzen oder entfernen (`null`): ändert weder Version noch Veröffentlichungsstand. */
export const ArticleGroupSetRequest = z.object({ groupId: z.string().regex(GROUP_ID).nullable(), expectedVersion: Version }).strict();
export type ArticleGroupSetRequest = z.infer<typeof ArticleGroupSetRequest>;
export const ArticleGroupSetResponse = z.object({ articleId: z.string(), groupId: z.string().nullable(), version: Version, updatedAt: IsoDateTime });
export type ArticleGroupSetResponse = z.infer<typeof ArticleGroupSetResponse>;
/** Namen vergleicht die API ohne Groß-/Kleinschreibung und Unicode-Varianten (wie Data). */
export function groupNameKey(name: string): string {
  return name.normalize('NFKC').trim().toLocaleLowerCase('de');
}

/**
 * „Alle App-2-Artikel löschen“ (Claude/Flori 29.10.2026): weiches Löschen in kleinen Wellen je Aufruf (der Löschport
 * braucht bis 8 s, die API-Funktion hat 10 s); die SPA ruft mit `after` weiter, bis `next` fehlt. `articleIds` wiederholt
 * gezielt Artikel mit Ergebnis `retry` (höchstens 500).
 */
export const ARTICLE_BULK_DELETE_MAX_IDS = 500;
export const ArticleBulkDeleteRequest = z
  .object({
    after: z.string().regex(ARTICLE_ID).optional(),
    articleIds: z.array(z.string().regex(ARTICLE_ID)).min(1).max(ARTICLE_BULK_DELETE_MAX_IDS).optional(),
    /** Vom Kunden bestätigt: Zuordnungen zu Angeboten vor dem Löschen lösen (nur zusammen mit `articleIds`). */
    unassignItems: z.literal(true).optional(),
  })
  .strict()
  .refine(r => !r.unassignItems || r.articleIds !== undefined, { message: 'unassignItems nur mit articleIds' });
export type ArticleBulkDeleteRequest = z.infer<typeof ArticleBulkDeleteRequest>;
/** `deleted`; `refused` (Grund bleibt, z. B. zugeordnet); `retry` (später erneut); `unknown` (Ausgang unklar, nie als gelöscht). */
export const ArticleDeleteOutcome = z.enum(['deleted', 'refused', 'retry', 'unknown']);
export type ArticleDeleteOutcome = z.infer<typeof ArticleDeleteOutcome>;
export const ArticleBulkDeleteResponse = z.object({
  results: z.array(z.object({ articleId: z.string(), title: z.string(), outcome: ArticleDeleteOutcome, code: z.string().max(64).optional() })),
  /** Fortsetzung für den nächsten Aufruf; fehlt = alle App-2-Artikel versucht. */
  next: z.string().optional(),
  /** Nicht gelöschte App-2-Artikel nach diesem Aufruf. */
  remaining: z.number().int().nonnegative(),
  /** Abbruch: unklarer Ausgang (nichts weiter versuchen) oder Konto lässt Löschen gerade nicht zu (Pause/Live-Update). */
  stopped: z.enum(['unknown-outcome', 'tenant-unavailable']).optional(),
});
export type ArticleBulkDeleteResponse = z.infer<typeof ArticleBulkDeleteResponse>;
/**
 * Einzelnen Artikel weich löschen (Flori 02.11.2026, Liste und Detailseite): `DELETE …/articles/:id?expectedVersion=<n>`,
 * Ergebnis wie ein Eintrag des Sammellöschens (Ablehnung mit statischem Grund, nie als Fehlerseite). Nur ein offener
 * Auftrag sperrt; zugeordnete Angebote löst `unassignItems=1` nach Bestätigung.
 */
export const ArticleDeleteQuery = z
  .object({
    expectedVersion: z.string().regex(/^[0-9]{1,9}$/).transform(Number),
    /** `1`: vom Kunden bestätigt, Zuordnungen zu Angeboten vorher lösen (die Beschreibungen bei eBay bleiben). */
    unassignItems: z.literal('1').optional(),
  })
  .strict();
export const ArticleDeleteResponse = z.object({ articleId: z.string(), outcome: ArticleDeleteOutcome, code: z.string().max(64).optional() });
export type ArticleDeleteResponse = z.infer<typeof ArticleDeleteResponse>;
/**
 * Aus dem Altsystem übernommen (App-BETA, Importprofil „Beta-Kunde“): der Import (`tools/migrate/import` `legacyArticle`)
 * übernimmt die Alt-Artikelnummer `artikel_id` als Kennung, dezimal wie core `DecimalId`; in der App angelegte Artikel
 * heißen immer `art-…` (`NEW_ARTICLE_ID`). Bekäme die App je numerische Kennungen, gälte stattdessen das Fehlen von
 * `appArticleCreateRequestHash` an der Zeile (packages/data `app-content.ts`).
 */
export function importedArticleNumber(articleId: string): string | undefined {
  return /^[1-9][0-9]{0,19}$/.test(articleId) ? articleId : undefined;
}

const Title = z.string().trim().min(1).max(255);
const Sku = z.string().trim().max(80);

/**
 * Neue Blöcke nehmen Bilder nur als Medium oder https-URL; Data-URLs aus dem Altsystem dürfen in
 * bestehenden Blöcken bleiben (core erlaubt sie übergangsweise), der Import wandelt sie um.
 */
export const ArticleCreateRequest = z
  .object({ articleId: z.string().regex(NEW_ARTICLE_ID), title: Title, sku: Sku.optional(), designId: z.string().max(64).optional() })
  .strict();
export type ArticleCreateRequest = z.infer<typeof ArticleCreateRequest>;
export const ArticleCreateResponse = z.object({ articleId: z.string() });
/** Artikel kopieren (Flori 29.10.2026): neue Kennung wie beim Anlegen, eine je Vorgang (Wiederholung idempotent). */
export const ArticleCopyRequest = z.object({ articleId: z.string().regex(NEW_ARTICLE_ID) }).strict();
export type ArticleCopyRequest = z.infer<typeof ArticleCopyRequest>;
/** Titel der Kopie: „Kopie von <Titel>“, auf die Titelgrenze gekürzt. */
export function copyTitle(title: string): string {
  return `Kopie von ${title}`.slice(0, 255).trimEnd();
}

export const ArticlePublishRequest = z.object({ expectedVersion: Version }).strict();

export const ArticlePublishResponse = ArticleDetailResponse.extend({ revise: ReviseState });
export type ArticlePublishResponse = z.infer<typeof ArticlePublishResponse>;
/**
 * Ausdrücklicher Schreibauftrag für genau ein Angebot (Flori 25.10.2026, Outbox `description.reconcile` reason
 * `customer-ordered`): PAT ersetzt die Beschreibung dieses Angebots durch den Artikel in genau dieser veröffentlichten
 * Fassung (core `articleVersion`); vorher sichert der Worker die bisherige Beschreibung. Abweichung beim Vormerken 409,
 * bei Ausführung terminal `article-changed`.
 */
export const ArticleItemOrderRequest = z.object({ publishedVersion: z.number().int().nonnegative() }).strict();
/** `deferred`: angenommen, die Übertragung an eBay ist in dieser Stage aber noch nicht freigegeben (nur dev). */
export const ArticleItemOrderResponse = z.object({ ordered: z.literal(true), deferred: z.literal(true).optional() });
export type ArticleItemOrderResponse = z.infer<typeof ArticleItemOrderResponse>;

/**
 * „HTML kopieren“ (App-BETA, `apps/app/docs/BETA_PLAN.md` §5): vollständiger Beschreibungscode der **veröffentlichten**
 * Fassung mit ihrer gebundenen Designfassung, zum Einfügen bei eBay. Galerieblöcke im Legacy-Format (`<!-- Start Premium
 * Auction Gallery "Name" [g] -->` … `ENDE`), damit das Legacy-Live-Update sie findet; `p=0`, Auswahl ohne Ausgangsangebot.
 * `galleries`: je Galerie der Designboxen die Zahl der Angebote im Code und, falls etwas fehlt, der Grund.
 */
export const ArticleExportGallery = z.object({
  galleryId: z.string(),
  name: z.string(),
  itemCount: z.number().int().nonnegative(),
  issue: z.enum(['not-found', 'not-imported', 'cross-selling-off', 'identity-missing', 'selection-unavailable', 'source-unavailable']).optional(),
});
/** `full` = Design, Bilder und Beschreibung; `description` = nur Beschreibung und Bilder (Legacy „HTML-Code Beschreibung“, z. B. Afterbuy/Plenty). */
export const ArticleExportVariant = z.enum(['full', 'description']);
export type ArticleExportVariant = z.infer<typeof ArticleExportVariant>;
export const ArticleExportResponse = z.object({
  html: z.string(),
  variant: ArticleExportVariant,
  articleVersion: Version,
  /** Gebundene Designfassung; fehlt bei `description` ohne Designbindung. */
  designVersion: Version.optional(),
  rendererVersion: z.string(),
  warnings: z.array(z.string()),
  galleries: z.array(ArticleExportGallery),
});
export type ArticleExportResponse = z.infer<typeof ArticleExportResponse>;

/** Höchstzahl angezeigter Angebote je Artikel. */
export const ARTICLE_ITEMS_LIMIT = 200;
/**
 * Höchstzahl tatsächlicher Änderungen je atomarem Schreibvorgang (DynamoDB: 100 Aktionen, davon 2
 * Bedingungsprüfungen auf Mandant und Artikel und seit dem Artikel-Löschschutz eine Lease-Prüfung; Festlegung
 * 24.10.2026, Codex-1 29.10.2026): Zuordnen braucht zusätzlich den Outbox-Eintrag (96), Lösen schreibt keinen (97).
 * Die API teilt größere Mengen auf.
 */
export const ATOMIC_ASSIGN_CHANGES = 96;
export const ATOMIC_UNASSIGN_CHANGES = 97;
export const ArticleItemsResponse = z.object({
  items: z.array(ItemView),
  /** Mehr als `ARTICLE_ITEMS_LIMIT` zugeordnet; angezeigt werden die neuesten. */
  truncated: z.boolean(),
  editable: z.boolean(),
});
export type ArticleItemsResponse = z.infer<typeof ArticleItemsResponse>;
export const ArticleItemsRequest = z
  .object({ add: z.array(EbayItemId).max(100).optional(), remove: z.array(EbayItemId).max(100).optional() })
  .strict()
  .refine(r => (r.add?.length ?? 0) + (r.remove?.length ?? 0) > 0, 'Keine Angebote angegeben');
export type ArticleItemsRequest = z.infer<typeof ArticleItemsRequest>;
export const ArticleItemsChangeResponse = ArticleItemsResponse.extend({
  /** Neu zugeordnet bzw. gelöst. Gelöste Angebote behalten ihre Beschreibung auf eBay. */
  added: z.array(EbayItemId),
  removed: z.array(EbayItemId),
  /** Angebote, die es im Konto nicht gibt. */
  missing: z.array(EbayItemId),
  revise: ReviseState,
});
export const ArticleSkuAssignResponse = ArticleItemsChangeResponse.extend({
  /** Aktive Angebote mit genau dieser SKU, die gefunden wurden. */
  matched: z.number().int().nonnegative(),
  /** Suche an der Obergrenze abgebrochen; es kann weitere Treffer geben. */
  searchTruncated: z.boolean(),
});
export type ArticleSkuAssignResponse = z.infer<typeof ArticleSkuAssignResponse>;
export type ArticleItemsChangeResponse = z.infer<typeof ArticleItemsChangeResponse>;

export const ArticleSaveRequest = z
  .object({
    expectedVersion: Version,
    title: Title,
    sku: Sku.optional(),
    designId: z.string().max(64).optional(),
    blocks: z.array(Block).max(200),
  })
  .strict();
export type ArticleSaveRequest = z.infer<typeof ArticleSaveRequest>;
